/**
 * Words and looks: an item's words (plain, or with looks of their own: `shared/richText.ts`), a
 * frame's title, and the style of the selection or the selected arrow.
 */
import type { GraphEdit } from "@protocol/GraphEdit";
import type { NodePatch } from "@protocol/NodePatch";
import { isRunKey, plainOf, readRich, styleRange, withoutKey, writeRich, type Run } from "../../shared/richText";
import type { BoardCtx } from "./context";
import { asPatch } from "./geometry";
import type { BoardPart } from "./types";
import { MIN_SIZE, TEXT_SIZE } from "./constants";

/** The style keys of a frame's layout: changing one lays out again what it holds. */
const LAYOUT_KEYS = ["layout", "gap", "padding", "align"];

/** The first `length` characters of `runs`. */
function cut(runs: readonly Run[], length: number): Run[] {
  let left = length;
  return runs.flatMap((r) => {
    if (left <= 0) return [];
    const text = r.text.slice(0, left);
    left -= text.length;
    return [{ text, style: r.style }];
  });
}

/** The words' and looks' members of the board service. */
export function wordsPart(ctx: BoardCtx): BoardPart<"setText" | "setRich" | "styleOf" | "setStyle"> {
  const { graph, view, actions, kinds } = ctx.deps;
  /** Whether node `kind` is drawn as free text. */
  const isText = (kind: string) => kinds.board(kind).element === "text";
  return {
    async setText(id, text) {
      const words = text.trim();
      const node = ctx.nodeOf(id);
      const group = ctx.groupOf(id);
      if (node) {
        if (words === node.name) return;
        if (!words) {
          if (isText(node.kind)) await actions.removeNode(id);
          return;
        }
        await actions.updateNode(id, { name: text.replace(/\s+$/, "") });
      } else if (group && words && words !== group.name) await actions.updateGroup(id, { name: words });
    },
    styleOf(target, key) {
      if (typeof target !== "string") return graph.state.graph.links.find((l) => l.from === target.from && l.to === target.to && l.kind === target.kind)?.style?.[key] ?? "";
      return (ctx.nodeOf(target)?.style ?? ctx.groupOf(target)?.style)?.[key] ?? "";
    },
    async setRich(id, runs, cleared = []) {
      const node = ctx.nodeOf(id);
      if (!node) return;
      const text = plainOf(runs).replace(/\s+$/, "");
      if (!text.trim()) {
        if (isText(node.kind)) await actions.removeNode(id);
        return;
      }
      // Spaces and line breaks after the last word go, as with plain words.
      const rich = writeRich(cut(runs, text.length));
      const style: Record<string, string> = Object.fromEntries(cleared.map((k) => [k, ""]));
      if (rich !== (node.style?.rich ?? "")) style.rich = rich;
      const patch: NodePatch = { ...(text !== node.name ? { name: text } : {}), ...(Object.keys(style).length ? { style } : {}) };
      if (Object.keys(patch).length) await actions.updateNode(id, patch);
    },
    async setStyle(key, value) {
      const s = view.state.selection;
      if (s?.type === "link") return void (await actions.updateLink(s.link, { style: { [key]: value } }));
      const edits: GraphEdit[] = ctx.selected().map((id): GraphEdit => {
        const node = ctx.nodeOf(id);
        if (!node) return { edit: "update_group", id, patch: { style: { [key]: value } } };
        // Words with looks of their own: the whole item's look wins again for this key (a highlight is only ever the words').
        if (isRunKey(key) && (node.style?.rich || key === "highlight")) {
          const runs = readRich(node.style?.rich, node.name);
          const rich = writeRich(key === "highlight" ? styleRange(runs, 0, plainOf(runs).length, key, value) : withoutKey(runs, key));
          if (key === "highlight") return { edit: "update_node", id, patch: { style: { rich } } };
          return { edit: "update_node", id, patch: { style: { [key]: value, rich } } };
        }
        // Free text grows and shrinks with its words' size, so bigger words are not cut off.
        const place = ctx.content().places.get(id);
        if (key === "size" && place && isText(node.kind)) {
          const before = Number(node.style?.size) || TEXT_SIZE;
          const after = Number(value) || TEXT_SIZE;
          const h = Math.max(MIN_SIZE, Math.round((place.h * after) / before));
          if (h !== place.h) return { edit: "update_node", id, patch: { style: { [key]: value }, board: asPatch({ ...place, h }) } };
        }
        return { edit: "update_node", id, patch: { style: { [key]: value } } };
      });
      // A frame's layout changing: what it holds is kept where the new layout shows it.
      for (const id of LAYOUT_KEYS.includes(key) ? ctx.selected().filter(ctx.isFrame) : []) {
        const style = { ...ctx.groupOf(id)?.style, [key]: value };
        if (ctx.laysOut(id, style)) edits.push(...ctx.relayEdits(id, new Map(), new Map(), new Map(), { [key]: value }));
      }
      await ctx.send(edits);
    },
  };
}
